import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

// Store
import AppState from '@webapp/store/state';
import { getFullName } from '@webapp/store/user/selectors';
import { CloseMenu } from '@webapp/store/ui/action-creators';
import {
  UpdateField,
  UpdateAnalyticsTimeframe,
  GetArtistAnalytics,
} from '@webapp/store/artist-analytics/action-creators';

// Types
import User from '@common/types/user/user';

//Utils
import { getDaysAndRange } from '@webapp/utils/analytics/filterFunctions';

// Transalte
import { Translate } from 'react-localize-redux';

// Controllers
import UserAvatarInfo from '@components/basics/user-avatar-info';

// Components
import Flex from '@components/layout/flex';
import NewMenu from '@components/new-menu';
import Typography from '@components/basics/typography';
import FilterIcon from '@components/basics/icons/filter';

import AnalyticsMenuContent from './menu-content';
import styled from 'styled-components';
import Button from '@components/actions/button';
import { filterSupportedPlatforms } from '@webapp/store/artist-analytics/selector';
import { AnalyticsQueryTypes } from '@services/analytics/types/analytics-query-enum';
import { hasActiveSubscription } from '@webapp/store/subscription-manager/selectors';

interface AnalyticsMenuProps extends Partial<User> {
  className?: string;
  isMobile?: boolean;
  analyticsRange: string;
  analyticsDsps: string[];
  isProArtist: boolean;
  updateRange: (value: string) => UpdateField<'range'>;
  updateDays: (value: string) => UpdateField<'days'>;
  updateDsps: (value: string[]) => UpdateField<'dsps'>;
  updateAnalyticsTimeframe: (
    start?: number,
    end?: number
  ) => UpdateAnalyticsTimeframe;
  getArtistAnalytics: (type: AnalyticsQueryTypes) => GetArtistAnalytics;
  closeMenu: () => CloseMenu;
  artistId: string;
}

const mapStateToProps = (state: AppState) => ({
  avatar: state.user.profile?.avatar,
  fullName: getFullName(state),
  isProArtist: hasActiveSubscription(state),
  analyticsRange: state?.artistAnalytics?.filters?.range || 'lastMonth',
  analyticsDsps:
    state.artistAnalytics?.filters?.dsps.length === 0
      ? filterSupportedPlatforms(state.categories.lists.platform || []).map(
          (p) => p.metadata.id
        )
      : state.artistAnalytics?.filters?.dsps,
  isMobile: state.ui.mobile,
  artistId: state.artist.active?.id || '',
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateRange: (value: string) =>
    dispatch(new UpdateField<'range'>('range', value)),
  updateDays: (value: string) =>
    dispatch(new UpdateField<'days'>('days', value)),
  updateDsps: (value: string[]) =>
    dispatch(new UpdateField<'dsps'>('dsps', value)),
  updateAnalyticsTimeframe: (start?: number, end?: number) =>
    dispatch(new UpdateAnalyticsTimeframe(start, end)),
  getArtistAnalytics: (type: AnalyticsQueryTypes) =>
    dispatch(new GetArtistAnalytics(type, undefined, true)),
  closeMenu: () => dispatch(new CloseMenu()),
});

export const AnalyticsMenu: React.FC<AnalyticsMenuProps> = ({
  className,
  avatar,
  fullName,
  isProArtist,
  updateDays,
  updateRange,
  updateDsps,
  updateAnalyticsTimeframe,
  getArtistAnalytics,
  closeMenu,
  analyticsRange,
  analyticsDsps,
  isMobile,
}) => {
  const [hasChanges] = React.useState(false);
  const [dateFilterRange, setDateFilterRange] = React.useState('');
  const [dspsFilter, setDspsFilter] = React.useState<string[]>(analyticsDsps);

  const applyFilter = () => {
    const [start, end, days] = getDaysAndRange(dateFilterRange);
    updateDays(days);
    updateRange(dateFilterRange);
    updateDsps(dspsFilter);
    updateAnalyticsTimeframe(start, end);
    getArtistAnalytics(AnalyticsQueryTypes.ENGAGEMENT);
    getArtistAnalytics(AnalyticsQueryTypes.COUNTS);
    getArtistAnalytics(AnalyticsQueryTypes.DSPS);
    getArtistAnalytics(AnalyticsQueryTypes.GENDER);
    getArtistAnalytics(AnalyticsQueryTypes.LOCATIONS);
    getArtistAnalytics(AnalyticsQueryTypes.SONGS);
    getArtistAnalytics(AnalyticsQueryTypes.AGE);
    closeMenu();
  };

  return (
    <NewMenu
      className={className}
      button={
        <PillButton
          className={hasChanges ? 'has-changes' : ''}
          alignItems="center"
          justifyContent="space-between"
          gap={10}
        >
          <Typography className="menu-btn-title" weight="700">
            <Translate id="ANALYTICS.MENU.BUTTON.LABEL" />
          </Typography>
          <FilterIcon invertedTheme={hasChanges} />
        </PillButton>
      }
      forcedMenu={!isProArtist ? 'DISTRIBUTION_TRIAL' : undefined}
      menuId="ANALYTICS_MENU"
      topHeader={
        isMobile && avatar && <UserAvatarInfo src={avatar} name={fullName!} />
      }
      header={
        !isMobile && avatar && <UserAvatarInfo src={avatar} name={fullName!} />
      }
      footer={
        <Button fullWidth onClick={applyFilter}>
          <Translate id="ANALYTICS.MENU.BUTTON.SAVE" />
        </Button>
      }
    >
      <AnalyticsMenuContent
        analyticsRange={analyticsRange}
        analyticsDsps={analyticsDsps}
        onDateRangeFilterChange={(value) => setDateFilterRange(value)}
        onDspsFilterChange={(value) => setDspsFilter(value)}
      />
    </NewMenu>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(AnalyticsMenu);

const PillButton = styled(Flex)`
  cursor: pointer;
  padding: 12px 18px;
  border-radius: 100px;
  opacity: 1;
  background-color: ${({ theme }) => theme.colors.midGray};
  transition: opacity
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    opacity: 0.75;
  }

  &.has-changes {
    background-color: ${({ theme }) => theme.colors.primary};

    .menu-btn-title {
      color: ${({ theme }) => theme.colors.differentBackground};
    }
  }
`;
